// Import API service
import { publicAPI } from '../services/apiService';

// Central location management utility
export const locationsManager = {
  // Get all locations from localStorage
  getAllLocations: () => {
    try {
      const savedLocations = localStorage.getItem('admin_locations');
      if (savedLocations) {
        return JSON.parse(savedLocations);
      }
      return [];
    } catch (error) {
      console.error('Error loading locations:', error);
      return [];
    }
  },

  // Get all locations from backend API
  getAllLocationsFromAPI: async () => {
    try {
      const response = await publicAPI.getLocations();
      const locations = response.data.locations || [];
      
      // Save to localStorage for offline access
      localStorage.setItem('admin_locations', JSON.stringify(locations));
      
      return locations;
    } catch (error) {
      console.error('Error loading locations from API:', error);
      // Fallback to localStorage
      return locationsManager.getAllLocations();
    }
  },

  // Get active locations for navigation menu
  getActiveLocations: () => {
    const locations = locationsManager.getAllLocations();
    return locations
      .filter(loc => loc.active)
      .sort((a, b) => a.sort_order - b.sort_order);
  },

  // Get location by slug
  getLocationBySlug: (slug) => {
    const locations = locationsManager.getAllLocations();
    return locations.find(loc => loc.slug === slug);
  },

  // Save locations to localStorage and notify all components
  saveLocations: (locations) => {
    try {
      localStorage.setItem('admin_locations', JSON.stringify(locations));
      
      // Dispatch event for reactive updates with delay to ensure all components can react
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('locations-updated', { 
          detail: { locations } 
        }));
      }, 100);
      
    } catch (error) {
      console.error('Error saving locations:', error);
    }
  },

  // Initialize locations from API
  initializeDefaultLocations: async () => {
    try {
      // Always try to load from API first
      const apiLocations = await locationsManager.getAllLocationsFromAPI();
      return apiLocations;
    } catch (error) {
      console.error('Error loading from API, using localStorage fallback:', error);
      // Fallback to localStorage
      return locationsManager.getAllLocations();
    }
  },

  // Initialize default locations if none exist (legacy)
  initializeDefaultLocationsLegacy: () => {
    // Fallback to localStorage or defaults
    const existing = locationsManager.getAllLocations();
    if (existing.length === 0) {
      const defaultLocations = [
        {
          id: 'gera',
          name: 'Gera',
          slug: 'gera',
          title_de: 'Immobilienfotograf in Gera | FotografX',
          title_ru: 'Фотограф недвижимости в Гере | FotografX',
          description_de: 'Professionelle Immobilienfotografie in Gera. Hochwertige Innen- und Außenaufnahmen, 360° virtuelle Rundgänge in Gera und Umgebung.',
          description_ru: 'Профессиональная фотография недвижимости в Гере. Качественные интерьерные и экстерьерные съемки, виртуальные туры 360° в Гере и окрестностях.',
          content_de: 'Gera ist eine wunderschöne Stadt in Thüringen mit vielen historischen Gebäuden und modernen Immobilien für professionelle Architekturfotografie.',
          content_ru: 'Гера - прекрасный город в Тюрингии с множеством исторических зданий и современной недвижимости для профессиональной архитектурной фотографии.',
          meta_keywords_de: 'Immobilienfotograf Gera, Architekturfotografie Gera, Innenaufnahmen Gera, 360° virtuelle Rundgänge Gera',
          meta_keywords_ru: 'фотограф недвижимости Гера, архитектурная фотография Гера, интерьерная съемка Гера, виртуальные туры 360° Гера',
          active: true,
          sort_order: 1
        },
        {
          id: 'ronneburg',
          name: 'Ronneburg',
          slug: 'ronneburg',
          title_de: 'Immobilienfotograf in Ronneburg | FotografX',
          title_ru: 'Фотограф недвижимости в Роннебурге | FotografX', 
          description_de: 'Ihr lokaler Immobilienfotograf in Ronneburg für hochwertige Innen- und Außenaufnahmen direkt vor Ihrer Haustür.',
          description_ru: 'Ваш местный фотограф недвижимости в Роннебурге для качественных интерьерных и экстерьерных съемок прямо у вашего порога.',
          content_de: 'Als lokaler Immobilienfotograf in Ronneburg kenne ich alle schönen Immobilien der Stadt und biete persönlichen Service für Makler und Eigentümer.',
          content_ru: 'Как местный фотограф недвижимости в Роннебурге, я знаю всю красивую недвижимость города und предлагаю персональный сервис для риелторов и собственников.',
          meta_keywords_de: 'Immobilienfotograf Ronneburg, lokaler Immobilienfotograf, Architekturfotografie Ronneburg',
          meta_keywords_ru: 'фотограф недвижимости Роннебург, местный фотограф недвижимости, архитектурная фотография Роннебург',
          active: true,
          sort_order: 2
        },
        {
          id: 'chemnitz',
          name: 'Chemnitz',
          slug: 'chemnitz',
          title_de: 'Immobilienfotograf in Chemnitz | FotografX',
          title_ru: 'Фотограф недвижимости в Хемнице | FotografX',
          description_de: 'Professioneller Immobilienfotograf in Chemnitz für Innen- und Außenaufnahmen, 360° virtuelle Rundgänge. Hochwertige Immobilienfotografie in Chemnitz und Umgebung.',
          description_ru: 'Профессиональный фотограф недвижимости в Хемнице для интерьерных и экстерьерных съемок, виртуальных туров 360°. Качественная фотография недвижимости в Хемнице и окрестностях.',
          content_de: 'Chemnitz bietet moderne Architektur und historische Gebäude für abwechslungsreiche Immobilienfotografie - von luxuriösen Wohnungen bis zu gewerblichen Objekten.',
          content_ru: 'Хемниц предлагает современную архитектуру и исторические здания для разнообразной фотографии недвижимости - от роскошных квартир до коммерческих объектов.',
          meta_keywords_de: 'Immobilienfotograf Chemnitz, Architekturfotografie Chemnitz, Wohnungsfotografie Chemnitz',
          meta_keywords_ru: 'фотограф недвижимости Хемниц, архитектурная фотография Хемниц, фотография квартир Хемниц',
          active: true,
          sort_order: 3
        },
        {
          id: 'erfurt',
          name: 'Erfurt',
          slug: 'erfurt',
          title_de: 'Immobilienfotograf in Erfurt | FotografX',
          title_ru: 'Фотограф недвижимости в Эрфурте | FotografX',
          description_de: 'Professionelle Immobilienfotografie in Erfurt - Innenaufnahmen, Außenaufnahmen und 360° virtuelle Rundgänge in Erfurt und Umgebung.',
          description_ru: 'Профессиональная фотография недвижимости в Эрфурте - интерьерные съемки, экстерьеры и виртуальные туры 360° в Эрфурте и окрестностях.',
          content_de: 'Erfurts wunderschöne Altstadt bietet einzigartige historische Kulissen für Ihre Immobilienfotografie - perfekt für luxuriöse Apartments und historische Gebäude.',
          content_ru: 'Красивый старый город Эрфурта предлагает уникальные исторические декорации для фотографии недвижимости - идеально для роскошных апартаментов и исторических зданий.',
          meta_keywords_de: 'Immobilienfotograf Erfurt, Architekturfotografie Erfurt, Altstadt Immobilien Erfurt',
          meta_keywords_ru: 'фотограф недвижимости Эрфурт, архитектурная фотография Эрфурт, недвижимость старого города Эрфурт',
          active: true,
          sort_order: 4
        },
        {
          id: 'leipzig',
          name: 'Leipzig',
          slug: 'leipzig',
          title_de: 'Immobilienfotograf in Leipzig | FotografX',
          title_ru: 'Фотограф недвижимости в Лейпциге | FotografX',
          description_de: 'Professionelle Immobilienfotografie in Leipzig für Innen- und Außenaufnahmen, 360° virtuelle Rundgänge. Hochwertige Immobilienfotografie in Leipzig und Umgebung.',
          description_ru: 'Профессиональная фотография недвижимости в Лейпциге для интерьерных и экстерьерных съемок, виртуальных туров 360°. Качественная фотография недвижимости в Лейпциге и окрестностях.',
          content_de: 'Leipzig bietet moderne Architektur und historische Gebäude für abwechslungsreiche Immobilienfotografie - von Gründerzeitvillen bis zu modernen Lofts.',
          content_ru: 'Лейпциг предлагает современную архитектуру и исторические здания для разнообразной фотографии недвижимости - от вилл времен основания до современных лофтов.',
          meta_keywords_de: 'Immobilienfotograf Leipzig, Architekturfotografie Leipzig, Loft Fotografie Leipzig',
          meta_keywords_ru: 'фотограф недвижимости Лейпциг, архитектурная фотография Лейпциг, фотография лофтов Лейпциг',
          active: true,
          sort_order: 5
        },
        {
          id: 'jena',
          name: 'Jena',
          slug: 'jena',
          title_de: 'Immobilienfotograf in Jena | FotografX',
          title_ru: 'Фотограф недвижимости в Йене | FotografX',
          description_de: 'Professionelle Immobilienfotografie in Jena für Innen- und Außenaufnahmen, 360° virtuelle Rundgänge. Spezialisiert auf Studentenwohnungen und historische Immobilien.',
          description_ru: 'Профессиональная фотография недвижимости в Йене для интерьерных und экстерьерных съемок, виртуальных туров 360°. Специализируется на студенческом жилье и исторической недвижимости.',
          content_de: 'Jena verbindet studentisches Leben mit historischer Architektur - perfekt für authentische Immobilienfotografie von Studentenwohnungen und historischen Gebäuden.',
          content_ru: 'Йена сочетает студенческую жизнь с исторической архитектурой - идеально для аутентичной фотографии недвижимости студенческого жилья и исторических зданий.',
          meta_keywords_de: 'Immobilienfotograf Jena, Studentenwohnung Fotografie Jena, historische Immobilien Jena',
          meta_keywords_ru: 'фотограф недвижимости Йена, фотография студенческого жилья Йена, историческая недвижимость Йена',
          active: true,
          sort_order: 6
        },
        {
          id: 'zwickau',
          name: 'Zwickau',
          slug: 'zwickau',
          title_de: 'Immobilienfotograf in Zwickau | FotografX',
          title_ru: 'Фотограф недвижимости в Цвикау | FotografX',
          description_de: 'Professionelle Immobilienfotografie in Zwickau für Innen- und Außenaufnahmen, 360° virtuelle Rundgänge. Hochwertige Immobilienfotografie in Zwickau und Umgebung.',
          description_ru: 'Профессиональная фотография недвижимости в Цвикау для интерьерных und экстерьерных съемок, виртуальных туров 360°. Качественная фотография недвижимости в Цвикау и окрестностях.',
          content_de: 'Zwickau bietet charmante Architektur und malerische Umgebung für wunderschöne Immobilienfotografie - von historischen Häusern bis zu modernen Apartments.',
          content_ru: 'Цвикау предлагает очаровательную архитектуру und живописную обстановку для прекрасной фотографии недвижимости - от исторических домов до современных апартаментов.',
          meta_keywords_de: 'Immobilienfotograf Zwickau, Architekturfotografie Zwickau, historische Häuser Zwickau',
          meta_keywords_ru: 'фотограф недвижимости Цвикау, архитектурная фотография Цвикау, исторические дома Цвикау',
          active: true,
          sort_order: 7
        }
      ];
      
      locationsManager.saveLocations(defaultLocations);
      return defaultLocations;
    }
    return existing;
  }
};

// Initialize on import
locationsManager.initializeDefaultLocations();